/**
 * Auth0 error codes that mean "this session needs renewing", not "something is
 * broken": an expired or revoked session, a grant the user has to answer
 * interactively, or a silent-renewal iframe that timed out.
 *
 * Nothing acts on an individual one. ApolloProvider's errorLink already redirects
 * to sign in on the same codes, and the socket reconnects indefinitely — so a
 * single stale session produced hundreds of identical events (WELD-FRONTEND-1DN,
 * -18H). Their *rate*, though, is the only signal an auth regression would give
 * us: a broken redirect that leaves users in a login loop looks exactly like
 * this, at volume. Hence grouped and downgraded rather than dropped, in
 * `monitoring/sentry.ts`.
 *
 * auth0-react's getAccessTokenSilently rethrows the underlying auth0-spa-js
 * error as-is when it is already an Error (normalizeErrorFn), so these arrive as
 * auth0-spa-js's own GenericError/TimeoutError/MissingRefreshTokenError. None of
 * them is an instance of the `OAuthError` exported by `@auth0/auth0-react` —
 * that is a separate class which only ever wraps redirect-callback payloads — so
 * match on the string `.error` code, never on the class.
 */
const BENIGN_AUTH_ERROR_CODES = new Set([
  "login_required",
  "invalid_grant",
  "consent_required",
  "mfa_required",
  "missing_refresh_token",
  "timeout",
]);

/**
 * The auth0 `.error` code carried by every auth0-spa-js error, if this is one.
 *
 * Restricted to Error instances — every auth0-spa-js error class extends Error —
 * because `{ error, error_description }` is also the shape of this app's own
 * `OAuthCodeExchangeError` socket payload, and a code like "timeout" is generic
 * enough to collide.
 */
export function getAuthErrorCode(e: unknown): string | undefined {
  if (
    e instanceof Error &&
    "error" in e &&
    typeof (e as { error: unknown }).error === "string"
  ) {
    return (e as unknown as { error: string }).error;
  }
  return undefined;
}

// auth0-spa-js's refresh-token fetch timeout is a plain Error with no `.error`
// code; no other dependency throws this exact message.
const AUTH0_FETCH_TIMEOUT_MESSAGE = "Timeout when executing 'fetch'";

/** The code, when it is one of the benign ones; `undefined` otherwise. */
export function getBenignAuthErrorCode(e: unknown): string | undefined {
  if (e instanceof Error && e.message === AUTH0_FETCH_TIMEOUT_MESSAGE) {
    return "timeout";
  }
  const code = getAuthErrorCode(e);
  return code !== undefined && BENIGN_AUTH_ERROR_CODES.has(code)
    ? code
    : undefined;
}
